import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { Role } from '@/types/settings-api';

interface UpdateRoleArgs {
  role: Role;
  tenantId: number;
  organizationId: string;
}

/**
 * Update a role (name, description, permission tree).
 *   PUT /api/TenantRoles/{id}?TenantId={tid}&organizationId={oid}
 *
 * Body shape was verified against a working dev cURL. The backend wants the
 * FULL Role JSON including every existing `permissiongrp[]` entry — only the
 * groups already on the role are included; new groups are not introduced
 * here (Flutter `edit_role_widget.dart` also only iterates the role's own
 * `permissiongrp` rather than the global catalogue).
 *
 * `permissionResults.id` is the backend PK for the per-role-per-permission
 * row (e.g. 37333). PRESERVE this value when toggling existing checkboxes —
 * losing it causes the backend to insert a duplicate row. For newly
 * activated permissions where there was no prior `permissionResults`, send
 * `id: null` per Flutter `edit_role_widget.dart:343` etc.
 */
export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ role, tenantId, organizationId }: UpdateRoleArgs) => {
      const qs = new URLSearchParams({
        TenantId: String(tenantId),
        organizationId,
      });
      return put<unknown>(
        `/api/TenantRoles/${role.roleId}?${qs.toString()}`,
        role,
      );
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles-list'] });
      queryClient.invalidateQueries({
        queryKey: ['settings', 'role-by-id', vars.role.roleId],
      });
    },
  });
}
